import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Bell, CheckCheck } from "lucide-react";

export default function MentionBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/mentions/unread-count");
      setCount(data.count || 0);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const openMenu = async () => {
    if (!open) {
      const { data } = await api.get("/mentions/me", { params: { limit: 10 } });
      setItems(data);
    }
    setOpen((o) => !o);
  };

  const markRead = async (id) => {
    await api.post(`/mentions/${id}/read`);
    setItems((p) => p.map((m) => (m.id === id ? { ...m, read: true } : m)));
    refresh();
  };

  const markAll = async () => {
    await api.post("/mentions/read-all");
    setItems((p) => p.map((m) => ({ ...m, read: true })));
    refresh();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          openMenu();
        }}
        data-testid="mention-bell-button"
        className="relative p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10"
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span
            data-testid="mention-bell-count"
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-[#FBBF24] text-[10px] font-bold text-white grid place-items-center"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="mention-bell-menu"
          className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-xl border border-[#15233B]/10 overflow-hidden z-50"
        >
          <div className="px-3 py-2 border-b border-[#15233B]/10 flex items-center gap-2 bg-[#F6F3EC]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
              Notifications
            </div>
            {items.some((m) => !m.read) && (
              <button
                onClick={markAll}
                data-testid="bell-mark-all-read"
                className="ml-auto text-[11px] font-semibold text-[#4F46E5] hover:underline inline-flex items-center gap-1"
              >
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-80 overflow-y-auto divide-y divide-[#15233B]/8">
            {items.length === 0 && (
              <li className="px-3 py-6 text-xs italic text-[#15233B]/40 text-center">
                Nothing new.
              </li>
            )}
            {items.map((m) => (
              <li
                key={m.id}
                className={`px-3 py-2 text-xs ${!m.read ? (m.source === "application" ? "bg-[#16A34A]/5" : "bg-[#4F46E5]/5") : ""}`}
                data-testid={`bell-mention-${m.id}`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: m.read ? "#cbd5e1" : m.source === "application" ? "#16A34A" : "#4F46E5" }}
                  />
                  <div className="flex-1 min-w-0">
                    {m.source === "application" ? (
                      <div className="font-bold text-[#15233B] truncate">
                        <span className="text-[#16A34A]">New applicant</span>{" "}
                        <span className="font-normal text-[#15233B]/60">·</span>{" "}
                        {m.card_full_name}
                      </div>
                    ) : (
                      <div className="font-bold text-[#15233B] truncate">
                        {m.by_user_name}{" "}
                        <span className="font-normal text-[#15233B]/60">on</span>{" "}
                        {m.card_full_name}
                      </div>
                    )}
                    <div className="text-[#15233B]/70 italic line-clamp-2">
                      {m.source === "application" ? m.text : `“${m.text}”`}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <Link
                        to="/inbox"
                        onClick={() => setOpen(false)}
                        className="text-[11px] font-semibold text-[#4F46E5] hover:underline"
                      >
                        Open inbox
                      </Link>
                      {!m.read && (
                        <button
                          onClick={() => markRead(m.id)}
                          className="text-[11px] font-semibold text-[#15233B]/60 hover:underline"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
